import { DEFAULT_INPUT_STATE, InputAction, InputState, SINGLE_ACTIONS } from '../input';

/**
 * Service for managing player input state.
 * Used by both client (single player) and server (multiple players).
 *
 * Input state is "frozen" at the start of each frame for consistent reads.
 * Single inputs are automatically reset after being consumed.
 */
export class InputService {
  // Live state - updated immediately on input
  private liveStates: Map<number, InputState> = new Map();

  // Frozen state - snapshot for current frame
  private frozenStates: Map<number, InputState> = new Map();

  // Pending changes to send to server (client-side)
  private pendingChanges: Map<number, { action: InputAction; value: boolean }[]> = new Map();

  // Track start times for actions to calculate duration
  private inputStartTimes: Map<number, Partial<Record<InputAction, number>>> = new Map();

  /**
   * Set an input action for a player.
   * Called on keydown/keyup (client) or when receiving input messages (server).
   */
  private readonly MAX_LATENCY_CORRECTION_MS = 200;

  public setInput(
    playerId: number,
    action: InputAction,
    value: boolean,
    correctionMs?: number,
  ): void {
    const state = this.getOrCreateLiveState(playerId);

    if (value) {
      // Key down - record start time
      this.getOrCreateStartTimes(playerId)[action] = Date.now();
    } else if (correctionMs !== undefined) {
      // Key up with duration - calculate correction
      const startTimes = this.getOrCreateStartTimes(playerId);
      const serverStartTime = startTimes[action];

      if (serverStartTime !== undefined) {
        const serverDuration = Date.now() - serverStartTime;
        const diff = correctionMs - serverDuration;

        // If client says they moved longer than we thought, add a correction
        if (diff > 0) {
          // Clamp to max to prevent massive jumps/exploits
          const correction = Math.min(diff, this.MAX_LATENCY_CORRECTION_MS);

          state.corrections = state.corrections || {};
          // Accumulate corrections if multiple happen in one frame (unlikely but possible)
          state.corrections[action] = (state.corrections[action] || 0) + correction;
        }
        delete startTimes[action];
      }
    }

    if (state[action] === value) return; // No change

    state[action] = value;

    // Track pending changes for network sync
    const changes = this.pendingChanges.get(playerId) ?? [];
    changes.push({ action, value });
    this.pendingChanges.set(playerId, changes);
  }

  /**
   * Freeze input state for all players at the start of a frame.
   * Systems should read from frozen state for consistency.
   */
  public freezeForFrame(): void {
    for (const [playerId, liveState] of this.liveStates) {
      // Deep copy input state including corrections
      this.frozenStates.set(playerId, {
        ...liveState,
        corrections: liveState.corrections ? { ...liveState.corrections } : undefined,
      });

      // Clear corrections from live state so they aren't applied twice
      if (liveState.corrections) {
        liveState.corrections = undefined;
      }
    }
  }

  /**
   * Get the frozen input state for a player.
   * Returns default state if player hasn't sent any inputs yet.
   */
  public getState(playerId: number): Readonly<InputState> {
    return this.frozenStates.get(playerId) ?? DEFAULT_INPUT_STATE;
  }

  /**
   * Get pending input changes for a player (for network sync).
   * Clears the pending changes after returning.
   */
  public consumePendingChanges(playerId: number): { action: InputAction; value: boolean }[] {
    const changes = this.pendingChanges.get(playerId) ?? [];
    this.pendingChanges.set(playerId, []);
    return changes;
  }

  /**
   * Reset single-press inputs for a player after a frame.
   * Called at the end of the game loop.
   */
  public consumeSingleInputs(playerId: number): void {
    const state = this.liveStates.get(playerId);
    if (state) {
      for (const action of SINGLE_ACTIONS) {
        state[action] = false;
      }
    }
  }

  /**
   * Get all player IDs that have input state.
   */
  public getAllPlayerIds(): number[] {
    return Array.from(this.liveStates.keys());
  }

  private getOrCreateLiveState(playerId: number): InputState {
    let state = this.liveStates.get(playerId);
    if (!state) {
      state = { ...DEFAULT_INPUT_STATE };
      this.liveStates.set(playerId, state);
    }
    return state;
  }

  private getOrCreateStartTimes(playerId: number): Partial<Record<InputAction, number>> {
    let times = this.inputStartTimes.get(playerId);
    if (!times) {
      times = {};
      this.inputStartTimes.set(playerId, times);
    }
    return times;
  }
}
